import { Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { memo, useCallback, useMemo } from 'react';

import { _getCategories } from '@/api/address-book/queries';
import EditIcon from '@/assets/svg/ui-elements/edit-outline.svg';
import { OPEN_CATEGORY_REQUIRED_REASON } from '@/features/address-book/locked-category';
import { CategoryAccordion } from '@/pages/address-book/modules/category-accordion';
import { selectsOpenCategory, toggleCategoryNode } from '@/pages/address-book/utils';
import { PagePaths } from '@/routes/page-paths';
import { Alert, Drawer, Spinner } from '@/shared/design-system';

import { StepBody } from './styled';

interface CategorySelectStepProps {
  contactName?: string;
  selectedIds: string[];
  /** The contact must be filed under at least one open category before it can be saved. */
  requireOpenCategory?: boolean;
  onSelectedIdsChange: (_ids: string[]) => void;
  onFinish: (_ids: string[]) => void;
  onBack: () => void;
  onClose: () => void;
}

export const CategorySelectStep = memo(
  ({
    contactName,
    selectedIds,
    requireOpenCategory = false,
    onSelectedIdsChange,
    onFinish,
    onBack,
    onClose,
  }: CategorySelectStepProps) => {
    const { data: categories = [], isLoading } = useQuery(_getCategories());
    const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

    const handleToggle = useCallback(
      (id: string) => {
        onSelectedIdsChange(toggleCategoryNode(selectedIds, categories, id));
      },
      [selectedIds, categories, onSelectedIdsChange],
    );

    const canSave =
      !requireOpenCategory || (!isLoading && selectsOpenCategory(categories, selectedIds));

    const handleManageCategories = useCallback(() => {
      window.open(PagePaths.addressBookCategories, '_blank', 'noopener,noreferrer');
    }, []);

    return (
      <Drawer
        open
        onOpenChange={(isOpen) => !isOpen && onClose()}
        title="Assign Categories"
        description={
          contactName
            ? `Select categories for ${contactName}`
            : 'Select categories for this contact'
        }
        maxWidth={600}
        onBack={onBack}
        leftAction={{
          children: (
            <>
              <EditIcon width={16} height={16} />
              Edit categories
            </>
          ),
          onClick: handleManageCategories,
        }}
        secondaryAction={{ children: 'Cancel', color: 'tertiary', onClick: onClose }}
        primaryAction={{
          children: 'Save',
          onClick: () => onFinish(selectedIds),
          disabled: !canSave,
        }}
      >
        <StepBody>
          {requireOpenCategory && (
            <Alert variant="info" size="md" description={OPEN_CATEGORY_REQUIRED_REASON} />
          )}
          {isLoading ? (
            <Stack padding="40px" alignItems="center" justifyContent="center">
              <Spinner />
            </Stack>
          ) : (
            <CategoryAccordion
              categories={categories}
              selectedIds={selectedSet}
              onToggle={handleToggle}
            />
          )}
        </StepBody>
      </Drawer>
    );
  },
);

CategorySelectStep.displayName = 'CategorySelectStep';
